import {
  getVisualStyleLabel,
  isVisualStyleLabelId,
  type VisualStyleLabelId,
} from '../style-labels/vocabulary';

export interface NarrativeLabelToken {
  labelId: VisualStyleLabelId;
  displayText: string;
}

export type NarrativePart =
  { type: 'text'; value: string } | { type: 'label'; value: string; labelId: VisualStyleLabelId };

const tokenPattern = /\[\[([a-z0-9_]+)\|([^\][<>]+)\]\]/g;
const prohibitedInference =
  /\b(personality|introvert|extrovert|anxious|wealthy|poor|masculine|feminine|gender|race|ethnicity|religion|sexuality|diagnosis|disability|attractive|age)\b/i;
const genericTemplate =
  /\b(unique personality|who you truly are|essence of you|speaks volumes about you|perfectly captures you)\b/i;

export const plainNarrative = (value: string) =>
  value.replace(tokenPattern, (_match, _id: string, label: string) => label);

export const parseStyleNarrative = (
  value: string,
  permittedIds?: Iterable<string>,
): { parts: NarrativePart[]; tokens: NarrativeLabelToken[]; safe: boolean } => {
  const permitted = permittedIds ? new Set(permittedIds) : null;
  const parts: NarrativePart[] = [];
  const tokens: NarrativeLabelToken[] = [];
  let cursor = 0;
  let match: RegExpExecArray | null;
  tokenPattern.lastIndex = 0;
  while ((match = tokenPattern.exec(value))) {
    if (match.index > cursor) parts.push({ type: 'text', value: value.slice(cursor, match.index) });
    const labelId = match[1]!;
    const displayText = match[2]!;
    if (isVisualStyleLabelId(labelId) && (!permitted || permitted.has(labelId))) {
      parts.push({ type: 'label', value: displayText, labelId });
      tokens.push({ labelId, displayText });
    } else {
      parts.push({ type: 'text', value: displayText });
    }
    cursor = match.index + match[0].length;
  }
  if (cursor < value.length) parts.push({ type: 'text', value: value.slice(cursor) });
  const unsafeMarkup = /<[^>]*>|javascript:|on\w+\s*=/i.test(value);
  return { parts, tokens, safe: !unsafeMarkup };
};

export const validateStyleNarrative = (
  value: string,
  coreLabelIds: readonly VisualStyleLabelId[],
  supportingLabelIds: readonly VisualStyleLabelId[] = [],
) => {
  const selectedLabelIds = [...coreLabelIds, ...supportingLabelIds];
  const parsed = parseStyleNarrative(value, selectedLabelIds);
  const words = plainNarrative(value).trim().split(/\s+/).filter(Boolean).length;
  const sentences = plainNarrative(value)
    .split(/[.!?]+(?:\s|$)/)
    .filter((part) => part.trim()).length;
  const tokenIds = parsed.tokens.map((token) => token.labelId);
  const errors: string[] = [];
  if (words < 45 || words > 90) errors.push('Narrative must contain 45–90 words.');
  if (sentences < 2 || sentences > 3) errors.push('Narrative must contain 2–3 sentences.');
  if (!parsed.safe) errors.push('Narrative contains unsafe markup.');
  if (prohibitedInference.test(value))
    errors.push('Narrative contains a prohibited personal inference.');
  if (genericTemplate.test(value)) errors.push('Narrative contains generic identity copy.');
  const labelsAreDistinct = new Set(selectedLabelIds).size === selectedLabelIds.length;
  const everySelectedLabelAppearsOnce = selectedLabelIds.every(
    (id) => tokenIds.filter((tokenId) => tokenId === id).length === 1,
  );
  const everyDisplayNameIsCanonical = parsed.tokens.every(
    ({ labelId, displayText }) => getVisualStyleLabel(labelId)?.label === displayText,
  );
  if (
    coreLabelIds.length !== 3 ||
    supportingLabelIds.length > 3 ||
    !labelsAreDistinct ||
    tokenIds.length !== selectedLabelIds.length ||
    !everySelectedLabelAppearsOnce ||
    !everyDisplayNameIsCanonical
  ) {
    errors.push('Narrative must mark up every selected controlled label exactly once.');
  }
  return { valid: errors.length === 0, errors, tokens: parsed.tokens };
};

const hash = (value: string) => {
  let result = 2166136261;
  for (const character of value) result = Math.imul(result ^ character.charCodeAt(0), 16777619);
  return result >>> 0;
};

const token = (id: VisualStyleLabelId) => {
  const label = getVisualStyleLabel(id)!;
  return `[[${id}|${label.label}]]`;
};

export const buildDeterministicStyleNarrative = (
  coreLabelIds: readonly [VisualStyleLabelId, VisualStyleLabelId, VisualStyleLabelId],
  supportingLabelIds: readonly VisualStyleLabelId[] = [],
) => {
  const [first, second, third] = coreLabelIds;
  const firstLabel = getVisualStyleLabel(first)!;
  const secondLabel = getVisualStyleLabel(second)!;
  const thirdLabel = getVisualStyleLabel(third)!;
  const supportingTokens = supportingLabelIds.slice(0, 3).map(token);
  const support =
    supportingTokens.length === 1
      ? `${supportingTokens[0]} sits at the edge of the reading, adding texture rather than another headline.`
      : supportingTokens.length === 2
        ? `${supportingTokens[0]} and ${supportingTokens[1]} sit at the edge of the reading, adding texture rather than another headline.`
        : supportingTokens.length === 3
          ? `${supportingTokens[0]}, ${supportingTokens[1]} and ${supportingTokens[2]} sit at the edge of the reading, adding texture rather than another headline.`
          : '';
  const endings = [
    support ||
      'The result stays open enough to hold several interpretations without losing its central idea.',
    support ||
      'Together, these references establish a specific visual direction while leaving room for context and change.',
    support ||
      'The combination is coherent, but it should still be treated as an editable visual reading.',
  ];
  const index = hash(coreLabelIds.join('|')) % endings.length;
  const secondFamily = secondLabel.family.replaceAll('_', ' ');
  const thirdFamily = thirdLabel.family.replaceAll('_', ' ');
  return `In this reading, ${token(first)} anchors the visual field across the full reference set. ${firstLabel.shortDescription} Together, ${token(second)} and ${token(third)} form a deliberate exchange between ${secondFamily} and ${thirdFamily}, giving the composition a more specific rhythm; ${endings[index]!.replace(/^./, (character) => character.toLocaleLowerCase())}`;
};
